/**
 * Run clues of a monochrome picture. Pure, mirrored from `apps/puzzletypes/picross/clues.py`;
 * the shared fixtures keep the two in step.
 */
import { EMPTY, FILLED, type Axis, type Clue } from "./types";

/** Lengths of the maximal runs of `#` along one line, left to right. */
export function runs(line: string): Clue {
  const result: number[] = [];
  let current = 0;
  for (const char of line) {
    if (char === FILLED) current += 1;
    else if (current > 0) {
      result.push(current);
      current = 0;
    }
  }
  if (current > 0) result.push(current);
  return result;
}

/** The picture read column by column, so every helper can work on a line. */
export function columns(rows: string[]): string[] {
  if (rows.length === 0) return [];
  const width = Math.max(...rows.map((row) => row.length));
  return Array.from({ length: width }, (_, c) =>
    rows.map((row) => (c < row.length ? row[c] : EMPTY)).join(""),
  );
}

/** The row and column clues a picture implies. */
export function cluesOf(rows: string[]): { rows: Clue[]; cols: Clue[] } {
  return { rows: rows.map(runs), cols: columns(rows).map(runs) };
}

/** Whether the runs, separated by at least one gap, can be laid along `length` cells. */
export function clueFits(clue: Clue, length: number): boolean {
  if (clue.length === 0) return true;
  return clue.reduce((a, b) => a + b, 0) + clue.length - 1 <= length;
}

function sameClue(a: Clue | undefined, b: Clue | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Every line whose runs differ from the declared clue. */
export function mismatchedLines(
  rows: string[],
  clues: { rows: Clue[]; cols: Clue[] },
): { line: Axis; index: number }[] {
  const actual = cluesOf(rows);
  const found: { line: Axis; index: number }[] = [];
  for (const axis of ["rows", "cols"] as const) {
    const expected = clues[axis] ?? [];
    const got = actual[axis];
    const count = Math.max(expected.length, got.length);
    for (let index = 0; index < count; index += 1) {
      if (!sameClue(expected[index], got[index]))
        found.push({ line: axis.slice(0, -1) as Axis, index });
    }
  }
  return found;
}

export function sum(clue: Clue): number {
  return clue.reduce((a, b) => a + b, 0);
}

/**
 * Whether a partially solved line can no longer satisfy its clue. Sound, not complete: a
 * filled run only grows, so the three tests below never flag a line that is still
 * completable (CLAUDE.md §9, `partial_check` rule). In doubt, stay silent. A crossed cell
 * (`x`) counts as decided empty; only `.` is undecided.
 */
export function lineContradiction(line: string, clue: Clue): boolean {
  const current = runs(line);
  const decided = !line.includes(EMPTY);
  if (decided) return !sameClue(current, clue);
  let filled = 0;
  for (const char of line) if (char === FILLED) filled += 1;
  if (filled > sum(clue)) return true;
  const longest = Math.max(0, ...current);
  const largest = Math.max(0, ...clue);
  return longest > largest;
}

/** Reads "2 1", "2,1" or "0" as an author types it; an unreadable token is dropped. */
export function parseClue(text: string): Clue {
  return text
    .split(/[\s,;]+/)
    .map((token) => Number.parseInt(token, 10))
    .filter((n) => Number.isFinite(n) && n > 0);
}

export function formatClue(clue: Clue): string {
  return clue.join(" ");
}
